import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { products, productsBySlug, type GenderedContent } from "../products";
import ProductCard from "../components/ProductCard";
import Seo from "../components/Seo";

const SITE = "https://olympusroids.com";

const LABEL_MARKERS = [
  "Common adverse reactions:",
  "Male specific adverse reactions:",
  "Female specific adverse reactions:",
  "General serious adverse reactions:",
  "Monitoring should include:",
  "Excipients:",
  "Each tablet contains:",
  "Each ml of",
];

function beautify(text: string | null | undefined): string {
  if (!text) return "";
  let out = text.trim();
  for (const marker of LABEL_MARKERS) {
    out = out.split(marker).join(`\n\n${marker}`);
  }
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

function hasGenderedContent(g: GenderedContent | null | undefined): g is GenderedContent {
  if (!g) return false;
  return Boolean(g.general || g.male || g.female);
}

function GenderedBlock({ data }: { data: GenderedContent }) {
  const entries: Array<{ label: string; key: "general" | "male" | "female"; tone: string }> = [
    { label: "All patients", key: "general", tone: "general" },
    { label: "Male", key: "male", tone: "male" },
    { label: "Female", key: "female", tone: "female" },
  ];
  const present = entries.filter((e) => data[e.key]);
  if (present.length === 1 && present[0]!.key === "general") {
    return <p className="detail__prose">{beautify(data.general)}</p>;
  }
  return (
    <div className="gendered">
      {present.map((e) => (
        <div key={e.key} className={`gendered__card gendered__card--${e.tone}`}>
          <div className="gendered__label">{e.label}</div>
          <p className="detail__prose">{beautify(data[e.key])}</p>
        </div>
      ))}
    </div>
  );
}

function ChemStat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="deep__stat">
      <div className="deep__stat-label">{label}</div>
      <div className={`deep__stat-value${mono ? " deep__stat-value--mono" : ""}`}>{value}</div>
    </div>
  );
}

function Caution({ title, text, tone }: { title: string; text: string; tone: string }) {
  return (
    <div className={`caution caution--${tone}`}>
      <div className="caution__title">{title}</div>
      <p className="caution__text">{beautify(text)}</p>
    </div>
  );
}

export default function Product() {
  const { slug } = useParams<{ slug: string }>();
  const product = slug ? productsBySlug[slug] : undefined;

  if (!product) {
    return (
      <section className="detail detail--empty">
        <Seo
          title="Formulation not found"
          description="The compound you're looking for is not in the OLYMPUS MEDS catalog."
          noindex
        />
        <div className="detail__inner">
          <h1 className="section-title">Formulation not found</h1>
          <p className="section-sub">The compound you're looking for is not in the catalog.</p>
          <Link to="/catalog" className="btn btn--primary">
            Return to Catalog →
          </Link>
        </div>
      </section>
    );
  }

  const d = product.details;
  const related = products
    .filter((p) => p.slug !== product.slug && p.category === product.category)
    .slice(0, 3);

  const hasChemistryExtras = d && (d.chemicalName || d.dosageForm);
  const hasCautions =
    d &&
    (d.contraindications || d.warning || d.specialPrecautions || d.drugInteractions || d.overdose || d.drivingEffect);

  // Build Drug JSON-LD schema for this product
  const drugLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Drug",
    name: product.proprietaryName,
    alternateName: product.pharmaceuticalName,
    activeIngredient: product.pharmaceuticalName,
    description: product.shortDescription,
    url: `https://olympusroids.com/product/${product.slug}`,
    manufacturer: {
      "@type": "Organization",
      name: "OLYMPUS MEDS",
      url: "https://olympusroids.com",
    },
    dosageForm: product.form,
    drugClass: product.classification,
    mechanismOfAction: d?.mechanism || product.mechanism,
    ...(product.image ? { image: product.image } : {}),
  };

  const seoTitle = `${product.proprietaryName} — ${product.pharmaceuticalName}`;
  const seoDescription = `${product.tagline} ${product.shortDescription}`
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 300);
  const seoKeywords = [
    product.proprietaryName,
    product.pharmaceuticalName,
    product.classification,
    product.form,
    product.category,
    "OLYMPUS MEDS",
    "Olympus Roids",
    "pharmaceutical grade",
  ].join(", ");

  return (
    <article className="detail">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={`/product/${product.slug}`}
        type="product"
        keywords={seoKeywords}
        image={product.image ?? undefined}
        jsonLd={drugLd}
      />
      <div className="detail__crumb">
        <Link to="/">Home</Link>
        <span>·</span>
        <Link to="/catalog">Catalog</Link>
        <span>·</span>
        <span className="detail__crumb-current">{product.proprietaryName}</span>
      </div>

      <section className="detail__hero">
        <div className="detail__visual" style={{ background: product.gradient }}>
          {product.image ? (
            <img
              src={product.image}
              alt={`${product.proprietaryName} — ${product.pharmaceuticalName} ${product.form.toLowerCase()} (${product.strength})`}
              className="detail__image"
            />
          ) : (
            <div className="detail__placeholder">
              <span>{product.proprietaryName.charAt(0)}</span>
            </div>
          )}
          <span className={`detail__badge detail__badge--${product.category.toLowerCase()}`}>
            {product.category}
          </span>
        </div>
        <div className="detail__primary">
          <div className="detail__meta">
            <span>{product.classification}</span>
            <span className="detail__dot">·</span>
            <span>{product.form}</span>
            <span className="detail__dot">·</span>
            <span>{product.strength}</span>
          </div>
          <h1 className="detail__name">{product.proprietaryName}</h1>
          <div className="detail__pharma">{product.pharmaceuticalName}</div>
          <p className="detail__tag">{product.tagline}</p>
          <p className="detail__short">{product.shortDescription}</p>
          <div className="detail__chemistry">
            {product.molecularFormula && (
              <div className="detail__chem">
                <div className="detail__chem-label">Molecular Formula</div>
                <div className="detail__chem-value">{product.molecularFormula}</div>
              </div>
            )}
            {product.molecularWeight && (
              <div className="detail__chem">
                <div className="detail__chem-label">Molecular Weight</div>
                <div className="detail__chem-value">{product.molecularWeight}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="detail__body">
        {hasChemistryExtras && (
          <div className="deep">
            <div className="deep__head">
              <span className="eyebrow">Molecular profile</span>
              <h2 className="section-title">Chemistry &amp; Composition</h2>
            </div>
            <div className="deep__grid">
              {d!.chemicalName && <ChemStat label="Chemical name" value={d!.chemicalName} mono />}
              {d!.molecularFormula && <ChemStat label="Molecular formula" value={d!.molecularFormula} mono />}
              {d!.molecularWeight && <ChemStat label="Molecular weight" value={d!.molecularWeight} mono />}
              {d!.dosageForm && <ChemStat label="Dosage form" value={d!.dosageForm} />}
              {d!.classification && <ChemStat label="Classification" value={d!.classification} />}
            </div>
            {d!.composition && (
              <div className="deep__composition">
                <div className="deep__stat-label">Composition</div>
                <p className="detail__prose">{beautify(d!.composition)}</p>
              </div>
            )}
          </div>
        )}

        <div className="detail__section">
          <span className="eyebrow">Pharmacodynamics</span>
          <h2 className="detail__h2">Mechanism of Action</h2>
          <p className="detail__prose">{beautify(d?.mechanism) || product.mechanism}</p>
        </div>

        {d?.pharmacokinetics && (
          <div className="detail__section">
            <span className="eyebrow">Absorption · Metabolism · Elimination</span>
            <h2 className="detail__h2">Pharmacokinetics</h2>
            <p className="detail__prose">{beautify(d.pharmacokinetics)}</p>
          </div>
        )}

        {hasGenderedContent(d?.indications) ? (
          <div className="detail__section">
            <span className="eyebrow">Clinical use</span>
            <h2 className="detail__h2">Therapeutic Indications</h2>
            <GenderedBlock data={d!.indications!} />
          </div>
        ) : (
          <div className="detail__section">
            <span className="eyebrow">Clinical use</span>
            <h2 className="detail__h2">Therapeutic Indications</h2>
            <ul className="detail__list">
              {product.indications.map((ind, i) => (
                <li key={i}>{ind}</li>
              ))}
            </ul>
          </div>
        )}

        {hasGenderedContent(d?.adverseReactions) && (
          <div className="detail__section">
            <span className="eyebrow">Safety profile</span>
            <h2 className="detail__h2">Adverse Reactions</h2>
            <GenderedBlock data={d!.adverseReactions!} />
          </div>
        )}

        {hasGenderedContent(d?.dosage) && (
          <div className="detail__section">
            <span className="eyebrow">Administration</span>
            <h2 className="detail__h2">Dosage &amp; Directions</h2>
            <GenderedBlock data={d!.dosage!} />
          </div>
        )}

        {hasCautions && (
          <div className="cautions">
            <div className="cautions__head">
              <span className="eyebrow">Clinical cautions</span>
              <h2 className="section-title">Risk &amp; interaction profile</h2>
            </div>
            <div className="cautions__grid">
              {d!.contraindications && (
                <Caution title="Contraindications" text={d!.contraindications} tone="red" />
              )}
              {d!.warning && <Caution title="Warnings" text={d!.warning} tone="red" />}
              {d!.specialPrecautions && (
                <Caution title="Special Precautions" text={d!.specialPrecautions} tone="amber" />
              )}
              {d!.drugInteractions && (
                <Caution title="Drug Interactions" text={d!.drugInteractions} tone="amber" />
              )}
              {d!.overdose && <Caution title="Overdose" text={d!.overdose} tone="neutral" />}
              {d!.drivingEffect && (
                <Caution title="Driving &amp; Machines" text={d!.drivingEffect} tone="neutral" />
              )}
            </div>
          </div>
        )}

        <div className="detail__dual">
          <div className="detail__section">
            <h2 className="detail__h2">Presentation</h2>
            <p className="detail__prose">{d?.presentation || product.presentation}</p>
          </div>
          <div className="detail__section">
            <h2 className="detail__h2">Storage</h2>
            <p className="detail__prose">{beautify(d?.storage) || product.storage}</p>
          </div>
        </div>

        <div className="auth-block">
          <div className="auth-block__qr">
            <QRCodeSVG
              value={`${SITE}/verify/${product.slug}`}
              size={128}
              level="M"
              bgColor="#ffffff"
              fgColor="#0b0b0f"
            />
          </div>
          <div className="auth-block__text">
            <div className="auth-block__eyebrow">Authentication</div>
            <h3 className="auth-block__title">Scan to verify authenticity</h3>
            <p className="auth-block__body">
              Every OLYMPUS MEDS vial carries a unique QR code. Scan the code on your packaging — or the code on
              the left — to confirm this product is a genuine {product.proprietaryName} formulation.
            </p>
            <Link to={`/verify/${product.slug}`} className="auth-block__cta">
              Open verification page →
            </Link>
          </div>
        </div>

        <div className="detail__notice">
          <span className="detail__notice-mark">⚠</span>
          <div>
            <div className="detail__notice-title">Reference only</div>
            <div className="detail__notice-text">
              This page is a product showcase for reference purposes. It is not medical advice, not a prescription,
              and nothing on this page is offered for sale. Consult a licensed physician for any therapeutic
              decision.
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="detail__related">
          <div className="detail__related-heading">
            <span className="eyebrow">More {product.category} formulations</span>
            <h2 className="section-title">Related formulations</h2>
          </div>
          <div className="product-grid">
            {related.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
